import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { PowerupType, POWERUP_CONFIGS as SHARED_POWERUP_CONFIGS, PowerupRarity } from '@wooorms/shared';
import { UIScaleManager } from '../utils/UIScaleManager';

// Re-export PowerupType for consumers of this module
export { PowerupType };

/**
 * Active powerup data
 */
export interface ActivePowerup {
  type: PowerupType;
  /** Remaining duration in seconds */
  remainingTime: number;
  /** Total duration in seconds (for progress calculation) */
  totalDuration: number;
  /** Stack count for stackable powerups (e.g., speed 1-3) */
  stackCount?: number;
}

/**
 * Powerup display configuration
 */
interface PowerupDisplayConfig {
  icon: string;
  color: number;
  label: string;
  rarity: PowerupRarity;
}

/**
 * Rarity color configurations
 */
const RARITY_COLORS = {
  [PowerupRarity.COMMON]: { border: 0x4ade80, glow: 0x22c55e },
  [PowerupRarity.UNCOMMON]: { border: 0x60a5fa, glow: 0x3b82f6 },
  [PowerupRarity.RARE]: { border: 0xfbbf24, glow: 0xf59e0b },
  [PowerupRarity.EPIC]: { border: 0xffa500, glow: 0xff8800 },  // Orange/Gold
  [PowerupRarity.LEGENDARY]: { border: 0xffd700, glow: 0xffcc00 },  // Bright Gold
  [PowerupRarity.MYTHIC]: { border: 0xff00ff, glow: 0xff66ff },  // Magenta/Pink
};

/**
 * Get display config for a powerup, handling stack counts for speed
 */
function getDisplayConfig(type: PowerupType, stackCount?: number): PowerupDisplayConfig {
  const sharedConfig = SHARED_POWERUP_CONFIGS[type];

  // Handle speed stacks with different icons
  if (type === PowerupType.SPEED && stackCount) {
    return {
      icon: `+${stackCount}`,
      color: sharedConfig.color,
      label: `Speed +${stackCount}`,
      rarity: sharedConfig.rarity,
    };
  }

  // Map shared config to display config with proper icons
  switch (type) {
    // Base Tier - Stack with each other
    case PowerupType.MULTIPLIER_2X:
      return { icon: '2×', color: sharedConfig.color, label: 'Score x2', rarity: sharedConfig.rarity };
    case PowerupType.MULTIPLIER_4X:
      return { icon: '4×', color: sharedConfig.color, label: 'Score x4', rarity: sharedConfig.rarity };
    // Final Slot - Only one active
    case PowerupType.MULTIPLIER_6X:
      return { icon: '6×', color: sharedConfig.color, label: 'Score x6', rarity: sharedConfig.rarity };
    // Override - Temporarily replaces Final Slot
    case PowerupType.MULTIPLIER_10X:
      return { icon: '10×', color: sharedConfig.color, label: 'Score x10', rarity: sharedConfig.rarity };
    case PowerupType.MULTIPLIER_15X:
      return { icon: '15×', color: sharedConfig.color, label: 'Score x15', rarity: sharedConfig.rarity };
    case PowerupType.MAGNET:
      return { icon: '◎', color: sharedConfig.color, label: 'Magnet', rarity: sharedConfig.rarity };
    case PowerupType.SPEED:
      return { icon: '⚡', color: sharedConfig.color, label: 'Speed +1', rarity: sharedConfig.rarity };
    case PowerupType.AGILITY:
      return { icon: '↻', color: sharedConfig.color, label: 'Agility', rarity: sharedConfig.rarity };
    case PowerupType.VISION:
      return { icon: '👁', color: sharedConfig.color, label: 'Vision', rarity: sharedConfig.rarity };
    default:
      return { icon: '?', color: 0x888888, label: 'Unknown', rarity: PowerupRarity.COMMON };
  }
}

/**
 * PowerupBar configuration
 */
export interface PowerupBarConfig {
  /** Item width */
  itemWidth: number;
  /** Item height */
  itemHeight: number;
  /** Gap between items */
  itemGap: number;
  /** Corner radius */
  cornerRadius: number;
  /** Background alpha */
  backgroundAlpha: number;
  /** Fade out duration when expiring (seconds) */
  fadeOutDuration: number;
  /** Enable glow effects */
  enableGlow: boolean;
  /** Enable pop-in animation */
  enablePopIn: boolean;
  /** Pop-in animation duration (seconds) */
  popInDuration: number;
}

/**
 * Default configuration - LARGER and more prominent!
 */
const DEFAULT_CONFIG: PowerupBarConfig = {
  itemWidth: 90,
  itemHeight: 90,
  itemGap: 16,
  cornerRadius: 45, // Circular items!
  backgroundAlpha: 0.95,
  fadeOutDuration: 5,
  enableGlow: true,
  enablePopIn: true,
  popInDuration: 0.35,
};

/**
 * Single powerup item display - circular, gamified design
 */
interface PowerupItem {
  container: Container;
  glowGraphics: Graphics;
  background: Graphics;
  borderGraphics: Graphics;
  circleProgress: Graphics; // Circular progress indicator
  iconText: Text;
  labelText: Text;
  timerText: Text;
  stackText: Text; // Shows "x2", "x3" etc for stacked powerups
  progressBar: Graphics;
  progressBarBg: Graphics;
  type: PowerupType;
  rarity: PowerupRarity;
  /** Animation state */
  animState: {
    spawnTime: number;
    scale: number;
    targetScale: number;
  };
}

/**
 * Collection notification
 */
interface CollectionNotification {
  container: Container;
  type: PowerupType;
  startTime: number;
  duration: number;
}

/**
 * PowerupBar - Displays active powerups with countdown timers
 *
 * Features:
 * - Horizontal bar at top of screen
 * - Shows active powerups with icons and countdown timers
 * - Color-coded by powerup type with rarity-based borders
 * - Glow effects and pop-in animations
 * - Timer countdown with progress bar
 * - Collection notifications
 */
export class PowerupBar {
  /** Main container */
  public container: Container;

  /** Configuration */
  private config: PowerupBarConfig;

  /** Active powerup items */
  private items: Map<PowerupType, PowerupItem> = new Map();

  /** Current active powerups data */
  private activePowerups: Map<PowerupType, ActivePowerup> = new Map();

  /** Whether bar is visible */
  private visible: boolean = false;

  /** Screen dimensions */
  private screenWidth: number = 0;

  /** Animation time */
  private animTime: number = 0;

  /** Collection notifications */
  private notifications: CollectionNotification[] = [];

  /** Notification container */
  private notificationContainer: Container;

  /** Items container (for proper layering) */
  private itemsContainer: Container;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  constructor(config: Partial<PowerupBarConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();

    // Create container
    this.container = new Container();
    this.container.label = 'powerup-bar';

    // Create items container
    this.itemsContainer = new Container();
    this.container.addChild(this.itemsContainer);

    // Create notification container (above items)
    this.notificationContainer = new Container();
    this.container.addChild(this.notificationContainer);

    // Initially hidden
    this.container.visible = false;
  }

  /**
   * Show the powerup bar
   */
  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  /**
   * Hide the powerup bar
   */
  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Handle screen resize
   */
  onResize(width: number, _height: number): void {
    this.screenWidth = width;
    this.updateLayout();
  }

  /**
   * Set active powerups
   */
  setActivePowerups(powerups: ActivePowerup[]): void {
    // Update or add new powerups
    const currentTypes = new Set<PowerupType>();

    for (const powerup of powerups) {
      currentTypes.add(powerup.type);
      const existing = this.activePowerups.get(powerup.type);

      // Check if this is a new powerup (not just an update)
      const isNew = !existing;

      this.activePowerups.set(powerup.type, { ...powerup });

      // Create item if doesn't exist
      if (!this.items.has(powerup.type)) {
        this.createPowerupItem(powerup.type, powerup.stackCount);

        // Show collection notification for new powerups
        if (isNew) {
          this.showCollectionNotification(powerup.type);
        }
      }
    }

    // Remove powerups that are no longer active
    const toRemove: PowerupType[] = [];
    this.activePowerups.forEach((_, type) => {
      if (!currentTypes.has(type)) {
        toRemove.push(type);
      }
    });

    toRemove.forEach(type => {
      this.removePowerupItem(type);
      this.activePowerups.delete(type);
    });

    // Update layout
    this.updateLayout();
  }

  /**
   * Add a single powerup
   */
  addPowerup(type: PowerupType, duration: number): void {
    const powerup: ActivePowerup = {
      type,
      remainingTime: duration,
      totalDuration: duration,
    };

    this.activePowerups.set(type, powerup);

    if (!this.items.has(type)) {
      this.createPowerupItem(type);
      this.showCollectionNotification(type);
    }

    this.updateLayout();
  }

  /**
   * Remove a powerup
   */
  removePowerup(type: PowerupType): void {
    this.activePowerups.delete(type);
    this.removePowerupItem(type);
    this.updateLayout();
  }

  /**
   * Create a powerup item display - CIRCULAR GAMIFIED DESIGN
   */
  private createPowerupItem(type: PowerupType, stackCount?: number): void {
    const { itemWidth, backgroundAlpha, enableGlow } = this.config;
    const displayConfig = getDisplayConfig(type, stackCount);
    const rarityColors = RARITY_COLORS[displayConfig.rarity];
    const radius = itemWidth / 2;
    const centerX = radius;
    const centerY = radius;

    // Create container for item
    const itemContainer = new Container();

    // Create glow graphics (behind everything) - CIRCULAR
    const glowGraphics = new Graphics();
    if (enableGlow) {
      const glowSize = radius * 0.4;
      glowGraphics.circle(centerX, centerY, radius + glowSize);
      glowGraphics.fill({ color: rarityColors.glow, alpha: 0.35 });
    }
    itemContainer.addChild(glowGraphics);

    // Create circular background
    const background = new Graphics();
    // Main background circle
    background.circle(centerX, centerY, radius - 2);
    background.fill({ color: displayConfig.color, alpha: backgroundAlpha });
    // Inner highlight arc for 3D effect
    background.arc(centerX, centerY - 2, radius - 8, -Math.PI * 0.8, -Math.PI * 0.2);
    background.stroke({ color: 0xffffff, width: 4, alpha: 0.25 });
    itemContainer.addChild(background);

    // Create circular progress indicator (track)
    const progressBarBg = new Graphics();
    progressBarBg.arc(centerX, centerY, radius - 4, 0, Math.PI * 2);
    progressBarBg.stroke({ color: 0x000000, width: 6, alpha: 0.4 });
    itemContainer.addChild(progressBarBg);

    // Create circular progress arc (filled portion)
    const circleProgress = new Graphics();
    itemContainer.addChild(circleProgress);

    // Create border with rarity color
    const borderGraphics = new Graphics();
    borderGraphics.circle(centerX, centerY, radius - 1);
    borderGraphics.stroke({ color: rarityColors.border, width: 4, alpha: 0.95 });
    itemContainer.addChild(borderGraphics);

    // Create icon text - LARGER
    const iconStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(32), // Bigger!
      fontWeight: 'bold',
      fill: 0xffffff,
      dropShadow: {
        color: 0x000000,
        blur: 6,
        distance: 2,
        alpha: 0.6,
      },
    });
    const iconText = new Text({ text: displayConfig.icon, style: iconStyle });
    iconText.anchor.set(0.5, 0.5);
    iconText.position.set(centerX, centerY - 8);
    itemContainer.addChild(iconText);

    // Create label text (small, at top)
    const labelStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(10),
      fontWeight: 'bold',
      fill: 0xffffff,
      letterSpacing: 0.5,
    });
    const labelText = new Text({ text: displayConfig.label.toUpperCase(), style: labelStyle });
    labelText.anchor.set(0.5, 0);
    labelText.position.set(centerX, 8);
    labelText.alpha = 0.9;
    itemContainer.addChild(labelText);

    // Create timer text - LARGER, PROMINENT
    const timerStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(16), // Much bigger!
      fontWeight: 'bold',
      fill: 0xffffff,
      dropShadow: {
        color: 0x000000,
        blur: 3,
        distance: 1,
        alpha: 0.5,
      },
    });
    const timerText = new Text({ text: '', style: timerStyle });
    timerText.anchor.set(0.5, 0.5);
    timerText.position.set(centerX, centerY + 18);
    itemContainer.addChild(timerText);

    // Create stack count indicator (bottom right corner)
    const stackStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(14),
      fontWeight: 'bold',
      fill: 0xffff00, // Yellow for visibility
      stroke: { color: 0x000000, width: 3 },
    });
    const stackText = new Text({ text: '', style: stackStyle });
    stackText.anchor.set(0.5, 0.5);
    stackText.position.set(centerX + radius * 0.6, centerY + radius * 0.5);
    stackText.visible = false; // Hidden until stacked
    itemContainer.addChild(stackText);

    // Legacy progress bar (hidden but kept for compatibility)
    const progressBar = new Graphics();
    progressBar.visible = false;
    itemContainer.addChild(progressBar);

    // Add to items container
    this.itemsContainer.addChild(itemContainer);

    // Store item with animation state
    this.items.set(type, {
      container: itemContainer,
      glowGraphics,
      background,
      borderGraphics,
      circleProgress,
      iconText,
      labelText,
      timerText,
      stackText,
      progressBar,
      progressBarBg,
      type,
      rarity: displayConfig.rarity,
      animState: {
        spawnTime: this.animTime,
        scale: 0.5,
        targetScale: 1,
      },
    });
  }

  /**
   * Show a collection notification
   */
  private showCollectionNotification(type: PowerupType): void {
    const displayConfig = getDisplayConfig(type);
    const rarityColors = RARITY_COLORS[displayConfig.rarity];

    // Create notification container
    const notifContainer = new Container();

    // Background pill
    const bg = new Graphics();
    const width = 160;
    const height = 36;
    bg.roundRect(-width / 2, -height / 2, width, height, height / 2);
    bg.fill({ color: 0x000000, alpha: 0.7 });
    bg.roundRect(-width / 2, -height / 2, width, height, height / 2);
    bg.stroke({ color: rarityColors.border, width: 2 });
    notifContainer.addChild(bg);

    // Icon
    const iconStyle = new TextStyle({
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(18),
      fontWeight: 'bold',
      fill: displayConfig.color,
    });
    const icon = new Text({ text: displayConfig.icon, style: iconStyle });
    icon.anchor.set(0.5, 0.5);
    icon.position.set(-width / 2 + 24, 0);
    notifContainer.addChild(icon);

    // Text
    const textStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(14),
      fontWeight: 'bold',
      fill: 0xffffff,
    });
    const text = new Text({ text: displayConfig.label, style: textStyle });
    text.anchor.set(0, 0.5);
    text.position.set(-width / 2 + 46, 0);
    notifContainer.addChild(text);

    // Position below the powerup icons (items are at Y=20 with height=90, so bottom is ~130)
    notifContainer.position.set(this.screenWidth / 2, 135);
    notifContainer.alpha = 0;
    notifContainer.scale.set(0.5);

    this.notificationContainer.addChild(notifContainer);

    // Add to notifications list
    this.notifications.push({
      container: notifContainer,
      type,
      startTime: this.animTime,
      duration: 2,
    });
  }

  /**
   * Remove a powerup item display
   */
  private removePowerupItem(type: PowerupType): void {
    const item = this.items.get(type);
    if (item) {
      this.itemsContainer.removeChild(item.container);
      item.glowGraphics.destroy();
      item.background.destroy();
      item.borderGraphics.destroy();
      item.circleProgress.destroy();
      item.iconText.destroy();
      item.labelText.destroy();
      item.timerText.destroy();
      item.stackText.destroy();
      item.progressBar.destroy();
      item.progressBarBg.destroy();
      item.container.destroy({ children: true });
      this.items.delete(type);
    }
  }

  /**
   * Update layout - position items horizontally centered
   */
  private updateLayout(): void {
    const { itemWidth, itemGap } = this.config;
    const itemCount = this.items.size;

    if (itemCount === 0) return;

    // Account for glow size in total width calculation
    const radius = itemWidth / 2;
    const glowSize = radius * 0.4;
    const itemTotalWidth = itemWidth + glowSize * 2;

    // Calculate total width
    const totalWidth = itemCount * itemTotalWidth + (itemCount - 1) * itemGap;

    // Start X position (centered)
    let currentX = (this.screenWidth - totalWidth) / 2;

    // Position each item
    this.items.forEach((item) => {
      item.container.position.set(currentX, 20);
      currentX += itemTotalWidth + itemGap;
    });
  }

  /**
   * Ease out cubic function for smooth animations
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Ease out elastic for bouncy effect
   */
  private easeOutElastic(t: number): number {
    const c4 = (2 * Math.PI) / 3;
    return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  }

  /**
   * Format time as m:ss or just seconds
   */
  private formatTime(seconds: number): string {
    const secs = Math.max(0, Math.ceil(seconds));
    if (secs >= 60) {
      const mins = Math.floor(secs / 60);
      const remainingSecs = secs % 60;
      return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  }

  /**
   * Update powerup timers and animations
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    // Update animation time
    this.animTime += dt;

    if (!this.visible) return;

    const { itemWidth, fadeOutDuration, popInDuration, enableGlow, enablePopIn } = this.config;
    const radius = itemWidth / 2;
    const centerX = radius;
    const centerY = radius;

    // Update each active powerup
    this.activePowerups.forEach((powerup, type) => {
      const item = this.items.get(type);
      if (!item) return;

      // Update remaining time
      powerup.remainingTime -= dt;

      // Pop-in animation
      if (enablePopIn) {
        const timeSinceSpawn = this.animTime - item.animState.spawnTime;
        if (timeSinceSpawn < popInDuration) {
          const progress = timeSinceSpawn / popInDuration;
          item.animState.scale = 0.5 + this.easeOutElastic(progress) * 0.5;
        } else {
          item.animState.scale = 1;
        }
        item.container.scale.set(item.animState.scale);
      }

      // Update timer text with nice formatting (m:ss for > 60s)
      item.timerText.text = this.formatTime(powerup.remainingTime);

      // Update stack count display
      if (powerup.stackCount && powerup.stackCount > 1) {
        item.stackText.text = `x${powerup.stackCount}`;
        item.stackText.visible = true;
      } else {
        item.stackText.visible = false;
      }

      // Update CIRCULAR progress arc
      const progress = Math.max(0, powerup.remainingTime / powerup.totalDuration);

      item.circleProgress.clear();
      if (progress > 0) {
        // Draw arc from top, going clockwise
        const startAngle = -Math.PI / 2; // Start at top
        const endAngle = startAngle + (progress * Math.PI * 2);

        // Color based on time remaining
        let arcColor = 0x4ade80; // Green
        if (progress < 0.2) {
          arcColor = 0xef4444; // Red when critical
        } else if (progress < 0.4) {
          arcColor = 0xfbbf24; // Yellow when low
        }

        item.circleProgress.arc(centerX, centerY, radius - 4, startAngle, endAngle);
        item.circleProgress.stroke({ color: arcColor, width: 6, alpha: 0.95 });

        // Inner glow effect on the arc
        item.circleProgress.arc(centerX, centerY, radius - 7, startAngle, endAngle);
        item.circleProgress.stroke({ color: 0xffffff, width: 2, alpha: 0.4 });
      }

      // Glow pulsing animation - MORE INTENSE
      if (enableGlow) {
        const pulseSpeed = powerup.remainingTime <= 10 ? 10 : 4;
        const baseGlow = powerup.remainingTime <= 10 ? 0.5 : 0.3;
        const glowPulse = baseGlow + Math.sin(this.animTime * pulseSpeed) * 0.2;
        item.glowGraphics.alpha = glowPulse;
      }

      // Urgent animation when expiring
      if (powerup.remainingTime <= fadeOutDuration) {
        const fadeProgress = powerup.remainingTime / fadeOutDuration;

        // Pulsing effect when critical
        if (powerup.remainingTime <= 5) {
          const pulse = Math.sin(this.animTime * 15) * 0.35 + 0.65;
          item.container.alpha = pulse;

          // Shake effect - more intense
          const shake = Math.sin(this.animTime * 35) * 3;
          item.iconText.position.x = centerX + shake;

          // Scale pulse for urgency
          const scalePulse = 1 + Math.sin(this.animTime * 10) * 0.05;
          item.container.scale.set(scalePulse);
        } else {
          item.container.alpha = 0.7 + fadeProgress * 0.3;
        }
      } else {
        item.container.alpha = 1;
        item.iconText.position.x = centerX;
      }
    });

    // Remove expired powerups
    const toRemove: PowerupType[] = [];
    this.activePowerups.forEach((powerup, type) => {
      if (powerup.remainingTime <= 0) {
        toRemove.push(type);
      }
    });

    toRemove.forEach(type => {
      this.removePowerup(type);
    });

    // Update notifications
    this.updateNotifications();
  }

  /**
   * Update collection notifications
   */
  private updateNotifications(): void {
    const toRemove: number[] = [];

    this.notifications.forEach((notif, index) => {
      const elapsed = this.animTime - notif.startTime;
      const progress = elapsed / notif.duration;

      if (progress >= 1) {
        toRemove.push(index);
        return;
      }

      // Animate notification
      if (progress < 0.2) {
        // Pop in
        const popProgress = progress / 0.2;
        notif.container.alpha = this.easeOutCubic(popProgress);
        notif.container.scale.set(0.5 + this.easeOutElastic(popProgress) * 0.5);
        notif.container.position.y = 135;
      } else if (progress > 0.7) {
        // Fade out and float down
        const fadeProgress = (progress - 0.7) / 0.3;
        notif.container.alpha = 1 - this.easeOutCubic(fadeProgress);
        notif.container.position.y = 135 + fadeProgress * 30;
      } else {
        notif.container.alpha = 1;
        notif.container.scale.set(1);
      }
    });

    // Remove finished notifications (reverse order to maintain indices)
    toRemove.reverse().forEach(index => {
      const notif = this.notifications[index];
      if (notif) {
        this.notificationContainer.removeChild(notif.container);
        notif.container.destroy({ children: true });
        this.notifications.splice(index, 1);
      }
    });
  }

  /**
   * Get active powerup types
   */
  getActivePowerups(): PowerupType[] {
    return Array.from(this.activePowerups.keys());
  }

  /**
   * Check if a powerup is active
   */
  hasPowerup(type: PowerupType): boolean {
    return this.activePowerups.has(type);
  }

  /**
   * Get remaining time for a powerup
   */
  getRemainingTime(type: PowerupType): number {
    return this.activePowerups.get(type)?.remainingTime ?? 0;
  }

  /**
   * Clear all powerups
   */
  clear(): void {
    this.items.forEach((_, type) => {
      this.removePowerupItem(type);
    });
    this.activePowerups.clear();

    // Clear notifications
    this.notifications.forEach(notif => {
      this.notificationContainer.removeChild(notif.container);
      notif.container.destroy({ children: true });
    });
    this.notifications = [];
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.clear();
    this.itemsContainer.destroy({ children: true });
    this.notificationContainer.destroy({ children: true });
    this.container.destroy({ children: true });
  }
}
